import { Injectable } from '@angular/core';
import { Filters } from '@core/models/room-filter/filters.enum';
import { MEAL_RELATED_FILTERS, RoomFilter } from '@core/models/room-filter/room-filter';
import { Rate } from '@core/models/Rate/Rate.model';
import { PrepaidRoomCluster } from '@core/models/PrepaidRoomCluster/PrepaidRoomCluster.model';
import { DisneyRoomCluster } from '@core/models/disney-room-cluster/DisneyRoomCluster.model';
import { ReqService } from '@core/services/req.service';
import { RoomFiltersConfig } from '@core/services/room-filters/room-filters-config';
import { CurrencyCode } from '../../../shared/enums/currency-code.enum';
import { SearchStateService } from '../search-state.service';

@Injectable({
  providedIn: 'root',
})
export class RoomFiltersService {
  private readonly ARG_CODE = 'NEW8';
  private readonly CONTRATO_DIRECTO_PROVIDERS = ['CVC_ARG', 'OMNIBEES_PULL', 'CONECTAAS'];

  constructor(
    private readonly contextService: ReqService,
    private readonly filterBuilder: RoomFiltersConfig,
    private readonly searchStateService: SearchStateService,
  ) {}

  public generateFilters(rooms: any, hotelInformation: any): Map<Filters, RoomFilter> {
    if (this.contextService.isDisneyHotel()) {
      return this.generateDisneyFilters(rooms);
    }

    const filtersMap: Map<Filters, RoomFilter> = new Map();

    const refundableFilter = this.filterBuilder.getBuilder(Filters.REFUNDABLE)(
      this.filterBuilder.FIlTER_ORDER[Filters.REFUNDABLE],
    );
    refundableFilter.disabled = true;
    filtersMap.set(Filters.REFUNDABLE, refundableFilter);

    const notRefundableFilter = this.filterBuilder.getBuilder(Filters.NOT_REFUNDABLE)(
      this.filterBuilder.FIlTER_ORDER[Filters.NOT_REFUNDABLE],
    );
    notRefundableFilter.disabled = true;
    filtersMap.set(Filters.NOT_REFUNDABLE, notRefundableFilter);

    const payAtDestinationFilter = this.filterBuilder.getBuilder(
      Filters.PAY_AT_DESTINATION,
    )(this.filterBuilder.FIlTER_ORDER[Filters.PAY_AT_DESTINATION]);
    payAtDestinationFilter.disabled = true;
    filtersMap.set(Filters.PAY_AT_DESTINATION, payAtDestinationFilter);

    let prepaidFilter: RoomFilter;
    const prepaidOrder = this.filterBuilder.FIlTER_ORDER[Filters.PREPAID];
    if (hotelInformation.country.id === this.ARG_CODE) {
      prepaidFilter = this.filterBuilder.getBuilder(Filters.PREPAID_NATIONAL)(
        prepaidOrder,
        Filters.PREPAID_NATIONAL,
      );
    } else {
      prepaidFilter = this.filterBuilder.getBuilder(Filters.PREPAID_INTERNATIONAL)(
        prepaidOrder,
        Filters.PREPAID_INTERNATIONAL,
      );
    }
    prepaidFilter.disabled = true;
    filtersMap.set(Filters.PREPAID, prepaidFilter);

    rooms.forEach(room => {
      room.rates.forEach((rate: Rate) => {
        // Planes de comida
        if (rate.meal_plan.code && Filters[rate.meal_plan.code]) {
          const mealPlanBuilder = this.filterBuilder.getBuilder(rate.meal_plan
            .code as any);
          const mealPlanOrder: number = this.filterBuilder.FIlTER_ORDER[
            rate.meal_plan.code
          ];
          filtersMap.set(
            Filters[rate.meal_plan.code],
            mealPlanBuilder(
              mealPlanOrder,
              rate.meal_plan.code as any,
              rate.meal_plan.description,
            ),
          );
        }

        // Politicas de cancelacion
        const refundPolicyKey: Filters = rate.refundable
          ? Filters.REFUNDABLE
          : Filters.NOT_REFUNDABLE;
        filtersMap.get(refundPolicyKey).disabled = false;

        // Contrato directo
        if (rate.provider && this.CONTRATO_DIRECTO_PROVIDERS.includes(rate.provider)) {
          if (!filtersMap.has(Filters.CONTRATO_DIRECTO)) {
            filtersMap.set(
              Filters.CONTRATO_DIRECTO,
              this.filterBuilder.getBuilder(Filters.CONTRATO_DIRECTO)(
                this.filterBuilder.FIlTER_ORDER[Filters.CONTRATO_DIRECTO],
              ),
            );
          }
        }

        // Forma de pago
        const paymentCode = rate.payment_method.code;
        if (paymentCode.toUpperCase() === 'PREPAID') {
          const currencyCode: string = rate.price.currency.code;
          if (currencyCode === CurrencyCode.USD) {
            filtersMap.get(Filters.PREPAID).label = 'Pago en dólares';
          }
          filtersMap.get(Filters.PREPAID).disabled = false;
        } else {
          filtersMap.get(Filters.PAY_AT_DESTINATION).disabled = false;
        }
      });
    });
    const mealRelatedKeys: Filters[] = [];
    for (const filter of filtersMap.values()) {
      if (MEAL_RELATED_FILTERS.includes(filter.filter)) {
        mealRelatedKeys.push(filter.filter);
      }

      if (
        filter.dependencies.length > 0 &&
        filter.dependencies.some(
          dependency => filtersMap.has(dependency) && filtersMap.get(dependency).disabled,
        )
      ) {
        filter.disabled = true;
      }
    }
    if (mealRelatedKeys.length === 1) {
      filtersMap.delete(mealRelatedKeys.pop());
    }

    // Check filters from URL
    if (this.searchStateService.flexible && filtersMap.has(Filters.REFUNDABLE)) {
      filtersMap.get(Filters.REFUNDABLE).active = !filtersMap.get(Filters.REFUNDABLE)
        .disabled;
    }

    if (
      this.searchStateService.paymentMethod &&
      (filtersMap.has(Filters.PREPAID) || filtersMap.has(Filters.PAY_AT_DESTINATION))
    ) {
      const paymentMethod = this.searchStateService.paymentMethod.toUpperCase();
      if (paymentMethod.includes('PREPAID')) {
        filtersMap.get(Filters.PREPAID).active = !filtersMap.get(Filters.PREPAID)
          .disabled;
      }
      if (paymentMethod.includes('PAY_AT_DESTINATION')) {
        filtersMap.get(Filters.PAY_AT_DESTINATION).active = !filtersMap.get(
          Filters.PAY_AT_DESTINATION,
        ).disabled;
      }
    }

    return filtersMap;
  }

  private generateDisneyFilters(
    disneyClusters: DisneyRoomCluster[],
  ): Map<Filters, RoomFilter> {
    const filtersMap: Map<Filters, RoomFilter> = new Map();

    disneyClusters.forEach(cluster => {
      const keys: Filters[] = [];
      if (cluster.hasTickets) {
        keys.push(Filters.DISNEY_TICKETS);
      }
      if (cluster.hasMealPlans) {
        keys.push(Filters.DISNEY_MEALPLANS);
      } else {
        keys.push(Filters.DISNEY_ROOM_ONLY);
      }
      keys.forEach(key => {
        const order = this.filterBuilder.FIlTER_ORDER[key];
        filtersMap.set(key, this.filterBuilder.getBuilder(key)(order, key));
      });
    });
    return filtersMap;
  }

  public filterRooms(rooms: PrepaidRoomCluster[], filters: RoomFilter[]) {
    if (this.contextService.isDisneyHotel()) {
      return this.filterDisneyRooms(rooms as any, filters);
    }
    const filteredRooms = [];
    rooms.forEach(room => {
      const rates = room.rates.filter(rate => {
        let match = false;
        filters.forEach(filter => {
          if (filter.filter.includes('PREPAID')) {
            if (rate.payment_method.code === 'PREPAID') {
              match = match || true;
            }
            return;
          }

          if (filter.filter === Filters.REFUNDABLE) {
            if (rate.refundable) {
              match = match || true;
            }
            return;
          }

          if (filter.filter === Filters.NOT_REFUNDABLE) {
            if (!rate.refundable) {
              match = match || true;
            }
            return;
          }

          if (filter.filter === Filters.PAY_AT_DESTINATION) {
            if (rate.payment_method.code === Filters.PAY_AT_DESTINATION) {
              match = match || true;
            }
            return;
          }

          if (filter.filter === Filters.CONTRATO_DIRECTO) {
            if (rate.provider && this.CONTRATO_DIRECTO_PROVIDERS.includes(rate.provider)) {
              match = match || true;
            }
            return;
          }

          if (MEAL_RELATED_FILTERS.includes(filter.filter)) {
            if (rate.meal_plan.code && rate.meal_plan.code === filter.filter) {
              match = match || true;
            }
            return;
          }
        });
        return match;
      });
      if (rates.length > 0) {
        const roomCopy = { ...room };
        roomCopy.rates = rates;
        filteredRooms.push(new PrepaidRoomCluster(roomCopy as any));
      }
    });
    return filteredRooms;
  }

  private filterDisneyRooms(
    rooms: DisneyRoomCluster[],
    filters: RoomFilter[],
  ): DisneyRoomCluster[] {
    return rooms.filter(room => {
      let match = false;
      filters.forEach(filter => {
        if (filter.filter === Filters.DISNEY_ROOM_ONLY) {
          if (room.hasRoomOnlyOption) {
            match = true;
          }
        }

        if (filter.filter === Filters.DISNEY_MEALPLANS) {
          if (room.hasMealPlans) {
            match = true;
          }
        }

        if (filter.filter === Filters.DISNEY_TICKETS) {
          match = room.hasTickets;
        }
      });
      return match;
    });
  }
}
